// Zoho Embedded App SDK: https://live.zwidgets.com/js-sdk/1.1/ZohoEmbededAppSDK.min.js
// All functions expect the ZOHO SDK instance (window.ZOHO) as the first parameter.

import axios from "axios";
import { EXTENSION_IDENTIFIER, SERVICE_API_URL } from "../config/serviceConstant";

const LOG_MODULE = `${EXTENSION_IDENTIFIER}__Shopify_Logs`;

// Zoho CRM field API names for the Shopify_Logs module
// Source: Zoho CRM → Settings → Modules → Shopify Logs → Fields
const FIELDS = {
  NAME: "Name",
  MODULE: `${EXTENSION_IDENTIFIER}__Module`,
  MODULE_RECORD_ID: `${EXTENSION_IDENTIFIER}__Module_Record_ID`,
  SHOPIFY_MODULE: `${EXTENSION_IDENTIFIER}__Shopify_Module`,
  SHOPIFY_RECORD_ID: `${EXTENSION_IDENTIFIER}__Shopify_Record_ID`,
  FROM_PLATFORM: `${EXTENSION_IDENTIFIER}__From_Platform`,
  TO_PLATFORM: `${EXTENSION_IDENTIFIER}__To_Platform`,
  STATUS: `${EXTENSION_IDENTIFIER}__Status`,
  REASON: `${EXTENSION_IDENTIFIER}__Reason`,
  SOURCE: `${EXTENSION_IDENTIFIER}__Source`,
  SETTING_ID: `${EXTENSION_IDENTIFIER}__Setting_ID`,
  REQUEST_BODY: `${EXTENSION_IDENTIFIER}__Request_Body`,
  SHOPIFY_URL: `${EXTENSION_IDENTIFIER}__Shopify_URL`,
};

// ── Retry with exponential back-off for rate-limit (429) errors ──

const withRetry = async (fn, retries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const isRateLimit =
        err?.status === 429 ||
        err?.status_code === 429 ||
        err?.data?.code === "TOO_MANY_REQUESTS" ||
        err?.data?.code === "RATE_LIMIT_EXCEEDED";

      if (!isRateLimit || attempt === retries) throw err;

      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 500;
      console.warn(
        `[logApi] Rate-limited, retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${retries})`
      );
      await new Promise((r) => setTimeout(r, delay));
    }
  }
};

// ── Helpers ──

/** Return an empty result set — used for 204 / NO_CONTENT responses */
const EMPTY = (page = 1) => ({
  records: [],
  info: { more_records: false, count: 0, page },
});

/**
 * Normalise the SDK response to a flat array of records.
 *
 * ZOHO.CRM.API.getAllRecords / searchRecord can return:
 *   - An array of objects            → use as-is
 *   - A single object (1 record)     → wrap in an array
 *   - null / undefined (0 records)   → empty array
 */
const toRecordArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "object" && (val.id || val.Id || val.Name)) return [val];
  return [];
};

/** Zoho returns 204 when a module has zero records — not an error. */
const isNoContent = (err) =>
  err?.status_code === 204 ||
  err?.status === 204 ||
  err?.code === "NO_CONTENT" ||
  err?.data?.code === "NO_CONTENT" ||
  (err?.data?.status === "error" && err?.data?.message?.includes("no content"));

// ── Public API ──

/**
 * Fetch paginated log records (newest first by default).
 *
 * @param {object} ZOHO - Zoho CRM SDK instance (window.ZOHO)
 * @param {{ page?: number, perPage?: number, sortBy?: string, sortOrder?: string }} params
 * @returns {{ records: object[], info: object }}
 */
export const getLogRecords = async (ZOHO, params = {}) => {
  const pg = params.page || 1;
  return withRetry(async () => {
    try {
      const resp = await ZOHO.CRM.API.getAllRecords({
        Entity: LOG_MODULE,
        sort_order: params.sortOrder || "desc",
        sort_by: params.sortBy || "Created_Time",
        page: pg,
        per_page: params.perPage || 200,
      });
      const records = toRecordArray(resp?.data);
      return {
        records,
        info: resp?.info || { more_records: false, count: records.length, page: pg },
      };
    } catch (err) {
      if (isNoContent(err)) return EMPTY(pg);
      throw err;
    }
  });
};

/**
 * Search log records using Zoho CRM criteria syntax.
 *
 * @param {object} ZOHO - Zoho CRM SDK instance (window.ZOHO)
 * @param {string} criteria - CRM criteria string
 * @param {{ page?: number, perPage?: number }} params
 */
export const searchLogRecords = async (ZOHO, criteria, params = {}) => {
  const pg = params.page || 1;
  return withRetry(async () => {
    try {
      const resp = await ZOHO.CRM.API.searchRecord({
        Entity: LOG_MODULE,
        Type: "criteria",
        Query: criteria,
        page: pg,
        per_page: params.perPage || 200,
      });
      const records = toRecordArray(resp?.data);
      return {
        records,
        info: resp?.info || { more_records: false, count: records.length, page: pg },
      };
    } catch (err) {
      if (isNoContent(err)) return EMPTY(pg);
      throw err;
    }
  });
};

/**
 * Free-text (word) search across all log fields.
 *
 * @param {object} ZOHO - Zoho CRM SDK instance (window.ZOHO)
 * @param {string} word
 * @param {{ page?: number, perPage?: number }} params
 */
export const wordSearchLogs = async (ZOHO, word, params = {}) => {
  const pg = params.page || 1;
  return withRetry(async () => {
    try {
      const resp = await ZOHO.CRM.API.searchRecord({
        Entity: LOG_MODULE,
        Type: "word",
        Query: word,
        page: pg,
        per_page: params.perPage || 200,
      });
      const records = toRecordArray(resp?.data);
      return {
        records,
        info: resp?.info || { more_records: false, count: records.length, page: pg },
      };
    } catch (err) {
      if (isNoContent(err)) return EMPTY(pg);
      throw err;
    }
  });
};

/**
 * Delete a single log record by ID.
 *
 * @param {object} ZOHO - Zoho CRM SDK instance (window.ZOHO)
 * @param {string} recordId
 * @returns {boolean}
 */
export const deleteLogRecord = async (ZOHO, recordId) => {
  return withRetry(async () => {
    const resp = await ZOHO.CRM.API.deleteRecord({
      Entity: LOG_MODULE,
      RecordID: recordId,
    });
    return resp?.data?.[0]?.code === "SUCCESS";
  });
};

/**
 * Build a CRM criteria string from a filter object.
 * Uses the actual Zoho CRM field API names from the Shopify_Logs module.
 *
 * @param {{ status?: string, module?: string, fromPlatform?: string, toPlatform?: string, nameContains?: string }} filters
 * @returns {string} criteria – empty string when no filters apply
 */
export const buildCriteria = (filters = {}) => {
  const parts = [];

  if (filters.status) {
    parts.push(`(${FIELDS.STATUS}:equals:${filters.status})`);
  }

  if (filters.module) {
    parts.push(`(${FIELDS.MODULE}:equals:${filters.module})`);
  }

  if (filters.fromPlatform) {
    parts.push(`(${FIELDS.FROM_PLATFORM}:equals:${filters.fromPlatform})`);
  }

  if (filters.toPlatform) {
    parts.push(`(${FIELDS.TO_PLATFORM}:equals:${filters.toPlatform})`);
  }

  if (filters.nameContains) {
    parts.push(`(Name:contains:${filters.nameContains})`);
  }

  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `(${parts.join("and")})`;
};

/**
 * Fetch the full sync "journey" of a single business record — every
 * Shopify_Logs row that references it — by OR-matching its CRM record ID and/or
 * its Shopify record ID.
 *
 * Used by the per-record Journey widget. Unlike `buildCriteria` (which joins
 * with `and`), this matches with `or` so a log written before the CRM record
 * ID existed (Shopify ID only) is still picked up, and vice-versa. Either key
 * may be empty; if both are empty the result is an empty set. Pages through all
 * results so a record with more than one page of logs returns its complete
 * history.
 *
 * @param {object} ZOHO - Zoho CRM SDK instance (window.ZOHO)
 * @param {{ moduleRecordId?: string, shopifyRecordId?: string, perPage?: number }} keys
 * @returns {{ records: object[], info: { more_records: boolean, count: number, page: number } }}
 */
export const getRecordJourney = async (
  ZOHO,
  { moduleRecordId, shopifyRecordId, perPage = 200 } = {}
) => {
  const clauses = [];
  if (moduleRecordId) clauses.push(`(${FIELDS.MODULE_RECORD_ID}:equals:${moduleRecordId})`);
  if (shopifyRecordId) clauses.push(`(${FIELDS.SHOPIFY_RECORD_ID}:equals:${shopifyRecordId})`);

  if (clauses.length === 0) return EMPTY(1);

  const criteria = clauses.length === 1 ? clauses[0] : `(${clauses.join("or")})`;

  const all = [];
  let page = 1;
  // Safety cap (50 pages × 200 = 10k logs) so a malformed `more_records` flag
  // can never spin into an infinite loop.
  for (let i = 0; i < 50; i++) {
    const { records, info } = await searchLogRecords(ZOHO, criteria, { page, perPage });
    all.push(...records);
    if (!info?.more_records) break;
    page += 1;
  }

  return { records: all, info: { more_records: false, count: all.length, page } };
};

// ── Retry outbound webhook ──

// The 9 dedicated webhook topics that have module-specific routes + body keys.
// Anything else falls through to the /webhook/custom/{topic} handler.
const DEDICATED_TOPICS = new Set([
  "customers/create",
  "customers/update",
  "customers/delete",
  "products/create",
  "products/update",
  "products/delete",
  "orders/create",
  "orders/updated",
  "orders/delete",
]);

// For dedicated routes: customers → shopify_customer_id, orders → shopify_order_id, …
const DEDICATED_ID_KEY = {
  customers: "shopify_customer_id",
  orders: "shopify_order_id",
  products: "shopify_product_id",
};

// Parse the action (create/update/delete) from the log `name` field, which
// follows the pattern "{shopifyModule}/{action} - {id}" (e.g. "customers/create - 10018...").
export const parseActionFromName = (name) => {
  if (!name || typeof name !== "string") return "";
  const head = name.split(" - ")[0] || "";
  const parts = head.split("/");
  return parts[1] ? parts[1].trim() : "";
};

/**
 * Retry a Shopify→Zoho sync by re-invoking the same outbound webhook the
 * Deluge function would call. Two routing paths, mirroring the Deluge code:
 *
 *   Dedicated topics (customers/*, orders/*, products/*):
 *     POST {SERVICE_API_URL}/webhook/{shopifyModule}/{action}
 *     body: { shopify_<module>_id, setting_id }
 *
 *   Custom / manually-created settings (everything else):
 *     POST {SERVICE_API_URL}/webhook/custom/{shopifyModule}/{action}
 *     body: { shopify_record_id, setting_id, webhook_topic }
 *
 * Headers are the same in both cases: orgid, apikey, connname, datacenterurl,
 * datacenter, shopifyshopurl, Content-Type.
 *
 * @param {object} headers  - { orgid, apikey, datacenterurl, datacenter, shopifyshopurl }
 * @param {object} log      - normalised log record (shopifyModule, shopifyRecordId, settingId, name)
 * @param {string} [action] - override for create/update/delete if log.name can't be parsed
 */
export const retryWebhook = async (headers, log, action) => {
  const shopifyModule = (log?.shopifyModule || "").toLowerCase();
  const act = (action || parseActionFromName(log?.name) || "").toLowerCase();
  if (!shopifyModule || !act) {
    throw new Error("Cannot retry: missing shopify module or action");
  }
  if (!log?.shopifyRecordId || !log?.settingId) {
    throw new Error("Cannot retry: missing shopify record id or setting id");
  }

  const topic = `${shopifyModule}/${act}`;
  const isDedicated = DEDICATED_TOPICS.has(topic);

  let url;
  let body;

  if (isDedicated) {
    url = `${SERVICE_API_URL}/webhook/${shopifyModule}/${act}`;
    const idKey = DEDICATED_ID_KEY[shopifyModule];
    body = {
      [idKey]: String(log.shopifyRecordId),
      setting_id: log.settingId,
    };
  } else {
    url = `${SERVICE_API_URL}/webhook/custom/${shopifyModule}/${act}`;
    body = {
      shopify_record_id: String(log.shopifyRecordId),
      setting_id: log.settingId,
      webhook_topic: topic,
    };
  }

  const reqHeaders = {
    orgid: headers.orgid,
    apikey: headers.apikey,
    connname: EXTENSION_IDENTIFIER,
    datacenterurl: headers.datacenterurl,
    datacenter: headers.datacenter,
    shopifyshopurl: headers.shopifyshopurl || log.shop || "",
    "Content-Type": "application/json",
  };

  // Pass the originating log record id so the backend can correlate the retry
  // attempt with the prior failure. Backend reads it as
  //   request.query?.log_record_id || request.query?.logRecordId
  const params = log?.id ? { log_record_id: log.id } : undefined;

  const resp = await axios({ method: "POST", url, headers: reqHeaders, data: body, params });
  return resp?.data;
};

export { LOG_MODULE, FIELDS };
